// client/src/coms/views/canvas_parts/canvasGameMode.js
// Game mode for the Canvas editor. Drawing games run the real Canvas engine (CanvasRoomView) on a private,
// local document; this module turns a game's drawing rules into restrictions layered over the engine:
// which engine tools may be used, colour and size limits, and the per-stroke record the game sends to the
// server with each picture. Party rules name tool families (pen, brush, eraser, fill, line, rect, ellipse);
// every engine tool belongs to one family, so a rule such as "shapes only" maps onto the real tools.

const FREEHAND = new Set(['pen', 'paint', 'liquify', 'smudge']);
const SHAPES = { line: 'line', rectangle: 'rect', ellipse: 'ellipse' };
// Tools that move or select pixels without adding any; always allowed.
const NEUTRAL = new Set(['grab', 'move', 'marquee_rect', 'marquee_ellipse', 'marquee_polygon', 'marquee_polygon_edit', 'selection']);

// The party family an engine tool belongs to ('' for neutral tools).
export function toolFamily(tool) {
  const value = String(tool || '');
  if (FREEHAND.has(value)) return 'pen';
  if (value === 'eraser') return 'eraser';
  if (value === 'bucket') return 'fill';
  if (value === 'text') return 'text';
  if (SHAPES[value]) return SHAPES[value];
  if (NEUTRAL.has(value)) return '';
  return 'other';
}

function hexToRgb(hex) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

export function normaliseHex(hex) {
  const rgb = hexToRgb(hex);
  return rgb ? `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}` : '';
}

function nearest(list, value, distance) {
  let best = list[0];
  let bestDistance = Infinity;
  list.forEach((candidate) => {
    const d = distance(candidate, value);
    if (d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  });
  return best;
}

function colourDistance(a, b) {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  if (!x || !y) return Infinity;
  return (x[0] - y[0]) ** 2 + (x[1] - y[1]) ** 2 + (x[2] - y[2]) ** 2;
}

// Restrictions for the engine from a game's rules. `forced` is the tool roulette entry in force right now.
export function resolveGameRestrictions(rules = {}, forced = null) {
  const families = Array.isArray(rules.tools) && rules.tools.length ? new Set(rules.tools.map(String)) : null;
  // 'pen' and 'brush' are one family on the engine: every freehand brush.
  if (families && families.has('brush')) families.add('pen');
  const forcedFamily = forced && forced.tool ? (forced.tool === 'brush' ? 'pen' : String(forced.tool)) : '';
  const palette = forced && forced.color
    ? [normaliseHex(forced.color)]
    : Array.isArray(rules.colors) && rules.colors.length ? rules.colors.map(normaliseHex).filter(Boolean) : null;
  const sizes = forced && forced.size
    ? [Number(forced.size)]
    : Array.isArray(rules.sizes) && rules.sizes.length ? rules.sizes.map(Number).filter((n) => n > 0) : null;
  return {
    families,
    forcedFamily,
    palette,
    sizes,
    allowText: Boolean(rules.allowText),
    allowImport: Boolean(rules.allowImport),
    noUndo: Boolean(rules.noUndo),
    noClear: Boolean(rules.noClear || rules.noUndo),
    maxStrokes: Math.max(0, Number(rules.maxStrokes) || 0),
    noStraight: Boolean(rules.noStraight),
    blind: Boolean(rules.blind),
    mirror: Boolean(rules.mirror),
    visual: Array.isArray(rules.visual) ? rules.visual : [],
  };
}

export function isToolAllowed(restrictions, tool) {
  if (!restrictions) return true;
  const family = toolFamily(tool);
  if (family === '') return true;
  if (family === 'other') return false;
  if (family === 'text') return restrictions.allowText && !restrictions.forcedFamily;
  if (restrictions.forcedFamily) return family === restrictions.forcedFamily || (restrictions.forcedFamily === 'pen' && family === 'pen');
  if (family === 'eraser' && restrictions.families && !restrictions.families.has('eraser')) return false;
  return !restrictions.families || restrictions.families.has(family);
}

// The engine tool to switch to when the current one is not allowed.
export function fallbackTool(restrictions) {
  const order = ['pen', 'rectangle', 'ellipse', 'line', 'bucket', 'eraser'];
  return order.find((tool) => isToolAllowed(restrictions, tool)) || 'grab';
}

export function snapColour(restrictions, colour) {
  if (!restrictions || !restrictions.palette || !restrictions.palette.length) return colour;
  const hex = normaliseHex(colour);
  if (restrictions.palette.includes(hex)) return hex;
  return nearest(restrictions.palette, hex || '#000000', colourDistance);
}

export function snapSize(restrictions, size) {
  if (!restrictions || !restrictions.sizes || !restrictions.sizes.length) return size;
  const value = Number(size) || 1;
  if (restrictions.sizes.includes(value)) return value;
  return nearest(restrictions.sizes, value, (a, b) => Math.abs(a - b));
}

// A marking stroke counts towards stroke limits; moving, selecting and panning do not.
export function isMarkingTool(tool) {
  const family = toolFamily(tool);
  return family !== '' && family !== 'other';
}

// A stroke counts as a straight line when no point strays more than 3% of its length from the chord
// (the same test the server applies to the points it is sent).
export function isStraightStroke(points) {
  if (!Array.isArray(points) || points.length < 3) return false;
  const [x0, y0] = points[0];
  const [x1, y1] = points[points.length - 1];
  const length = Math.hypot(x1 - x0, y1 - y0);
  if (length < 60) return false;
  let worst = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const [x, y] = points[i];
    const d = Math.abs((y1 - y0) * x - (x1 - x0) * y + x1 * y0 - y1 * x0) / length;
    if (d > worst) worst = d;
  }
  return worst < length * 0.03;
}

// Up to `max` evenly spaced points of a stroke, rounded, for the server's straight-line check.
export function samplePoints(points, max = 48) {
  if (!Array.isArray(points) || !points.length) return [];
  const step = Math.max(1, Math.ceil(points.length / max));
  const out = [];
  for (let i = 0; i < points.length; i += step) out.push([Math.round(points[i][0]), Math.round(points[i][1])]);
  const last = points[points.length - 1];
  if (out.length && (out[out.length - 1][0] !== Math.round(last[0]) || out[out.length - 1][1] !== Math.round(last[1]))) {
    out.push([Math.round(last[0]), Math.round(last[1])]);
  }
  return out;
}

// The per-drawing document id the engine runs under: negative, so the engine never talks to the Canvas
// server about it (every Canvas API call requires a positive canvas id).
export function gameDocumentId(key) {
  let hash = 0;
  const text = String(key || 'game');
  for (let i = 0; i < text.length; i += 1) hash = ((hash * 31) + text.charCodeAt(i)) | 0;
  return -(Math.abs(hash % 2000000000) + 1);
}
